import { PageDS, ToastContainerDS } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import "../../../../i18n/i18n";
import { useNavigate, useParams } from "react-router-dom";
import { AddUserRole } from "./addUserRole";
import { ManagerCreationRole, UserUpdateType } from "../usersAPI";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import { selectManagersList, updateUserRoleAsync } from "../usersSlice";
import { AddUserDepartmentModal } from "./addUserDepartmentModal";
import { DepartmentType } from "../../departments/departmentsApi";

type Steps = "role" | "informations" | "department";

export type UserInformations = {
  firstName: string;
  lastName: string;
  email: string;
  job: string;
};

export const UpdateUserRolePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const currentManagerUuid = params.id;

  const [step, setStep] = useState<Steps>("role");
  const company = useAppSelector(selectCompany);
  const managerList = useAppSelector(selectManagersList);
  const currentManager = managerList.find((m) => m.uuid === currentManagerUuid);

  const onClose = useCallback(() => {
    navigate(`/home/users/management/${currentManagerUuid}`);
  }, [currentManagerUuid, navigate]);

  const onSubmit = useCallback(
    (role: ManagerCreationRole, userDepartments?: DepartmentType[]) => {
      if (!company || !role || !currentManagerUuid) return;
      if (
        role === "DEPARTMENT_USER" &&
        (!userDepartments || userDepartments.length === 0)
      ) {
        onClose();
        return;
      }
      const userUpdate: UserUpdateType = {
        managerUuid: currentManagerUuid,
        role,
        departmentsAccess: null,
        companiesAccess: [],
      };

      if (role === "DEPARTMENT_USER" && userDepartments) {
        userUpdate.departmentsAccess = userDepartments.map(
          (department) => department.uuid,
        );
      }

      if (role !== "DEPARTMENT_USER") {
        if (currentManager?.role !== "DEPARTMENT_USER")
          userUpdate.companiesAccess = currentManager?.companiesAccess || [
            company.uuid,
          ];
        else userUpdate.companiesAccess = [company.uuid];
      }

      onClose();
      void dispatch(updateUserRoleAsync(userUpdate));
    },
    [company, currentManagerUuid, dispatch, onClose, currentManager],
  );

  const onSubmitUserRole = useCallback(
    (role: "ADMIN" | "USER" | "DEPARTMENT_USER") => {
      if (role !== "DEPARTMENT_USER") onSubmit(role);
      else setStep("department");
    },
    [onSubmit],
  );

  const onSubmitUserDepartment = useCallback(
    (departments: DepartmentType[]) => {
      onSubmit("DEPARTMENT_USER", departments);
    },
    [onSubmit],
  );

  return (
    <PageDS
      title={t("users.modal.updateRole.title") || ""}
      toaster={<ToastContainerDS />}
      titleElement={{
        closeButtonAction: onClose,
      }}
      withoutSupport
    >
      {step === "role" ? (
        <AddUserRole
          currentManager={currentManager}
          onSubmit={onSubmitUserRole}
          onReturn={onClose}
          isUpdate
        />
      ) : (
        <></>
      )}
      {step === "department" ? (
        <AddUserDepartmentModal
          onReturn={() => setStep("role")}
          onSubmit={onSubmitUserDepartment}
          isUpdate
        />
      ) : (
        <></>
      )}
    </PageDS>
  );
};
